<template>
  <div id="chartFileManagerDiv">
    <div>
      <button @click="resetChartButtonClick()">reset chart</button>
    </div>
    <div>
      <input type="text" ref="importDataInput" @click="e => e.target.select()" style="width: 60px;" />
      or
      <input type="file" @change="importFileChange" />
      <button @click="importButtonClick()">import</button>
    </div>
    <br>
    <div>
      <button @click="saveInIndexedDBButtonClick()">save in indexed db (locally)</button>
    </div>
    <div>
      export format
      <select v-model="selectedExportFormat">
        <option v-for="[k, v] in Object.entries(ImportExportChartType)" :key="k" :value="v">{{ k }}</option>
      </select>
    </div>
    <div>
      <button @click="exportChartButtonClick('file')">export chart (file)</button>
    </div>
    <div>
      <button @click="exportChartButtonClick('clipboard')">export chart (clipboard)</button>
    </div>

    <div>
      <p>chart title: <input type="text" @input="setChartTitle" ref="chartTitleInput" /></p>
    </div>

    <br>

    <div v-for="key in idbStore.currentAudioChartTitles" :key="key">
      <button @click="chartRemoveButtonClick(key)">x</button>
      <button @click="chartLoadButtonClick(key)">{{ !key || key == "" ? "no title" : key }}</button>

    </div>
  </div>
</template>

<script setup>
import { inject, ref, useTemplateRef, watch } from 'vue';
import { useAudioStore } from '../../stores/AudioStore';
import { useIDBStore } from '../../stores/IDBStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { useLogStore } from '../../stores/LogStore';
import { exportToClipboard, exportToFile, identifyChartStrFormat, exportChart, importChart } from '@/util/importExportHelpers';
import { useToastStore } from '../../stores/ToastStore';
import { EventLineType, ImportExportChartType } from '@/util/constants';
import { useKeyboardEventStore } from '@/stores/KeyboardEventStore';

const chartTitleInput = useTemplateRef("chartTitleInput")
const importDataInput = useTemplateRef("importDataInput")
const selectedExportFormat = ref('danoni')

const selectionStore = useSelectionStore()
const mainChart = inject('mainChart')
const idbStore = useIDBStore()
const logStore = useLogStore()
const audioStore = useAudioStore()
const toastStore = useToastStore()
const keyboardEventStore = useKeyboardEventStore()

const LOG_GROUP = "File manager"

const saveInIndexedDBButtonClick = e => {
  const [dataStr, err] = exportChart(mainChart, ImportExportChartType.Cat2edito)
  if (err != null) {
    toastStore.show("Failed to convert chart to string!", "error")
    logStore.sendLog(LOG_GROUP, `Failed to convert chart to string: ${err}`, err)
    return
  }
  idbStore.setChartDataItem(chartTitleInput.value.value, dataStr)
}

keyboardEventStore.eventBusSubscribe(e => {
  if(e.type === 'save') saveInIndexedDBButtonClick()
})

const exportChartButtonClick = async target => {
  const chartFormat = selectedExportFormat.value
  const [dataStr, err] = exportChart(mainChart, chartFormat)

  if (err != null) {
    toastStore.show("Failed to export chart!", "error")
    logStore.sendLog(LOG_GROUP, `Failed to export chart: ${err}`, err)
    // throw err
    return
  }

  if (target == 'file') {
    const title = `${audioStore.currentAudioTitle ? audioStore.currentAudioTitle : 'unknown'} | ${chartTitleInput.value.value ? chartTitleInput.value.value : 'unknown'}`
    const err = exportToFile(title, dataStr, chartFormat)
    if (err === null) {
      logStore.sendLog(LOG_GROUP, `Successfully exported chart!`)
      toastStore.show("Exported chart!", "success")
    } else {
      toastStore.show("Failed to export to file!", "error")
      logStore.sendLog(LOG_GROUP, `Failed to export to file: ${err}`, err)
    }
  }
  else {
    const err = await exportToClipboard(dataStr)
    if (err === null) {
      toastStore.show("Copied to clipboard!", "success")
      logStore.sendLog(LOG_GROUP, 'Exported chart data copied to clipboard');
    } else {
      toastStore.show("Failed to export to clipboard!", "error")
      logStore.sendLog(LOG_GROUP, `Failed to export to clipboard: ${err}`, err)
    }
  }

}

const chartRemoveButtonClick = key => {
  idbStore.removeChartDataItem(key)
}

const chartLoadButtonClick = key => {
  idbStore.getChartDataValue(key).then(e => {
    const err = importChart(mainChart, e, ImportExportChartType.Cat2edito)
    if (err != null) {
      logStore.sendLog(LOG_GROUP, `Failed to load chart!`, err)
      // throw err
      return
    }
    chartTitleInput.value.value = key
    selectionStore.clearSelection()
  })
}

const resetChartButtonClick = () => {
  if (window.confirm("The chart will be reset. Are you sure?")) {
    mainChart.resetChart()
    selectionStore.clearSelection()
    mainChart.addEventLine(0, EventLineType.Timing)
  }
}

let importFile = null

const importData = (str) => {
  const err = importChart(mainChart, str)
  if (err != null) {
    logStore.sendLog(LOG_GROUP, `Failed to import chart!`, err)
    // throw err
    return
  }
  logStore.sendLog(LOG_GROUP, `Successfully imported chart`)
}

const importButtonClick = () => {
  // get chart str
  let data = importDataInput.value.value
  if (data == "" && importFile != null) {
    logStore.sendLog(LOG_GROUP, "Starting reading of the import file")
    const reader = new FileReader();
    reader.onload = () => {
      logStore.sendLog(LOG_GROUP, "Success reading the import file", reader.result)
      importData(reader.result)
    };
    reader.onerror = () => {
      logStore.sendLog(LOG_GROUP, "Error reading the import file!")
    };
    reader.readAsText(importFile);
  } else {
    importData(data)
  }
}

const importFileChange = e => {
  importFile = e.target.files?.[0] ?? null
}

</script>
